import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE } from '@/lib/utils';
// import type { S3Object } from '@/lib/zybooks-api';
import { uploadImageToS3, type UploadedImageResponse } from '@/lib/zybooks-api';
import { CloudUpload } from 'lucide-react';
import { useRef, useState } from 'react';
interface UploadImageModalProps {
    open: boolean;
    onClose: () => void;
    onUploadSuccess: (uploadedFile: UploadedImageResponse) => void;
    onUploadFailure?: (error?: Error) => void;
}

interface ImageUploadDragAreaProps {
    onFile: (file: File) => void;
    children?: React.ReactNode;
}

const ImageUploadDragArea: React.FC<ImageUploadDragAreaProps> = ({ onFile, children }) => {
    const [dragover, setDragover] = useState(false);

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        setDragover(false);
        e.preventDefault();
        e.stopPropagation();

        const file = Array.from(e.dataTransfer.files);
        if (file.length > 1) {
            return;
        }
        onFile(file[0]);
    };

    const onDragover = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragover(true);
    };

    const onDragleave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragover(false);
    };

    return (
        <div
            className={`tiptap-image-upload-dragger ${dragover ? 'tiptap-image-upload-dragger-active' : ''}`}
            onDrop={onDrop}
            onDragOver={onDragover}
            onDragLeave={onDragleave}
        >
            {children}
        </div>
    );
};

const DropZoneContent: React.FC<{ maxSize: number }> = ({ maxSize }) => (
    <>
        <div className="mb-4 flex justify-center">
            <CloudUpload />
        </div>
        <div className="text-center">
            <h3 className="mb-2 text-lg font-medium">Drag and drop an image here</h3>
            <p className="text-sm text-muted-foreground">or click to browse</p>
            <p className="mt-2 text-xs text-muted-foreground">Maximum file size: {maxSize / 1024} KB</p>
        </div>
    </>
);

export const UploadImageModal: React.FC<UploadImageModalProps> = props => {
    const { open, onClose, onUploadSuccess, onUploadFailure } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fileName, setFileName] = useState<string>('');
    const abortControllerRef = useRef<AbortController | null>(null);

    const handleUpload = async (file: File) => {
        if (file.size > MAX_FILE_SIZE) {
            onUploadFailure?.(new Error(`File size exceeds maximum allowed (${MAX_FILE_SIZE / 1024} KB)`));
            return;
        }
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            onUploadFailure?.(new Error(`File type not accepted (${file.type})`));
            return;
        }
        try {
            setIsUploading(true);
            setUploadProgress(0);
            setFileName(file.name);
            abortControllerRef.current = new AbortController();

            const uploadedFile: UploadedImageResponse = await uploadImageToS3(
                file,
                undefined,
                event => setUploadProgress(event.progress),
                abortControllerRef.current.signal,
            );
            if (uploadedFile.success) {
                onUploadSuccess(uploadedFile);
            } else {
                throw new Error(uploadedFile.error);
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                // Upload was cancelled
                console.log('Upload cancelled');
            } else {
                console.error('Error uploading image:', error);
            }
            onUploadFailure?.(error as Error);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            setFileName('');
            onClose();
        }
    };

    const handleCancel = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleUpload(files[0]);
        }
    };
    const handleClick = () => {
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.click();
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={onClose}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Upload Image</DialogTitle>
                </DialogHeader>
                <div
                    className="tiptap-image-upload-dropzone-container"
                    onClick={!isUploading ? handleClick : undefined}
                >
                    {!isUploading && (
                        <div className="border-2 border-dashed border-muted-foreground rounded-md p-6">
                            <ImageUploadDragArea
                                onFile={file => {
                                    handleUpload(file);
                                }}
                            >
                                <DropZoneContent maxSize={MAX_FILE_SIZE} />
                            </ImageUploadDragArea>
                        </div>
                    )}
                    {isUploading && (
                        <div className="flex w-full flex-col items-center gap-4 p-6">
                            <CloudUpload className="h-10 w-10 animate-pulse text-primary" />
                            <div className="w-full space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="max-w-[200px] truncate font-medium">{fileName}</span>
                                    <span className="text-muted-foreground">{uploadProgress}%</span>
                                </div>
                                <Progress
                                    value={uploadProgress}
                                    className="h-2 w-full"
                                />
                            </div>
                            <Button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleCancel();
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </div>
                <input
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES}
                    ref={inputRef}
                    className="hidden"
                    onChange={handleInputChange}
                />
            </DialogContent>
        </Dialog>
    );
};
